import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BellRinging, CaretDown, CaretUp, Eye, EyeSlash } from "@phosphor-icons/react";
import { StatusBadge } from "./Badges";
import { formatAcres } from "../lib/incidents";
import FollowFireButton from "./FollowFireButton";
import InlineShareButton from "./InlineShareButton";
import { shareOrigin } from "../lib/shareOrigin";
import { buildIncidentCaption, evacsForShare } from "../lib/shareCaption";

export const FollowedFiresPanel = ({ fires = [], onRemoved, showOnMap = true, onToggleShowOnMap, evacAlerts = [], shareLogs = {} }) => {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("fw_following_collapsed") === "1"
  );
  if (!fires.length) return null;
  const toggle = () => {
    setCollapsed((v) => {
      localStorage.setItem("fw_following_collapsed", v ? "0" : "1");
      return !v;
    });
  };
  return (
    <div className="border-b border-tac-border" data-testid="followed-fires-panel">
      <button
        type="button"
        data-testid="followed-fires-panel-toggle"
        onClick={toggle}
        title={collapsed ? "Show followed fires" : "Hide followed fires"}
        className="w-full px-4 py-2 flex items-center justify-between bg-tac-primary/10 border-b border-tac-primary/30 hover:bg-tac-primary/15 transition-colors"
      >
        <div className="flex items-center gap-2">
          <BellRinging size={13} weight="fill" className="text-tac-primary" />
          <span className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-primary">
            Following
          </span>
        </div>
        <div className="flex items-center gap-2">
          {onToggleShowOnMap && (
            <span
              role="button"
              tabIndex={0}
              data-testid="followed-map-visibility-toggle"
              title={showOnMap ? "Hide followed fires on the live map (clean view for videos)" : "Show followed fires on the live map"}
              onClick={(e) => { e.stopPropagation(); onToggleShowOnMap(); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onToggleShowOnMap(); } }}
              className={`p-1 border rounded-sm transition-colors ${
                showOnMap
                  ? "border-tac-primary/50 text-tac-primary hover:bg-tac-primary/10"
                  : "border-tac-border text-tac-faint hover:text-tac-text"
              }`}
            >
              {showOnMap ? <Eye size={13} weight="bold" /> : <EyeSlash size={13} weight="bold" />}
            </span>
          )}
          <span className="text-[15px] font-mono-tac tabular text-tac-dim">{fires.length}</span>
          {collapsed ? (
            <CaretDown size={13} weight="bold" className="text-tac-primary" />
          ) : (
            <CaretUp size={13} weight="bold" className="text-tac-primary" />
          )}
        </div>
      </button>
      {!collapsed && fires.map((f) => {
        const evacs = evacsForShare(evacAlerts, f.latitude, f.longitude, f.name);
        const ref = new Date(shareLogs[f.id] || Date.now() - 24 * 36e5);
        const freshEvacs = evacs.filter(
          (e) =>
            (e.created_at && new Date(e.created_at) > ref) ||
            (e.updated_at && new Date(e.updated_at) > ref)
        ).length;
        return (
        <div
          key={f.id}
          data-testid={`followed-fire-${f.id}`}
          className="px-4 py-2.5 border-b border-tac-border/60 last:border-b-0 flex items-start justify-between gap-2 hover:bg-tac-surface/60 transition-colors"
        >
          <Link to={`/incident/${f.id}`} className="min-w-0 flex-1 group">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-bold text-tac-text group-hover:text-tac-primary transition-colors">
                {f.name}
              </span>
              <StatusBadge status={f.status_label} />
              {freshEvacs > 0 && (
                <span
                  data-testid={`blast-nudge-${f.id}`}
                  title={`${freshEvacs} evac zone${freshEvacs === 1 ? "" : "s"} new or updated since your last blast — re-share this fire`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm border animate-pulse"
                  style={{ color: "#FFB703", borderColor: "#FFB70366", background: "#FFB70314" }}
                >
                  🔄 {freshEvacs} evac update{freshEvacs === 1 ? "" : "s"} — re-share
                </span>
              )}
            </div>
            <div className="text-[13px] font-mono-tac tabular text-tac-dim mt-0.5" data-testid={`followed-fire-meta-${f.id}`}>
              {f.acres != null ? `${formatAcres(f.acres)} ac` : "— ac"}
              {f.county ? ` · ${f.county.replace("_", " ")} Co.` : ""}
              {f.ref ? (
                <span className="text-tac-primary font-bold">
                  {" "}· {f.edge
                    ? `${f.edge.distance_mi.toFixed(1)} mi to fire edge`
                    : `${f.ref.distance_mi.toFixed(1)} mi`}{" "}
                  {(f.edge?.compass || f.ref.compass)} of {f.ref.ref_label}
                </span>
              ) : null}
            </div>
          </Link>
          {(() => {
            const shareUrl = `${shareOrigin()}/api/share/incident/${f.id}`;
            return (
              <div className="flex items-center gap-2 shrink-0 mt-0.5">
                <FollowFireButton
                  incidentId={f.id}
                  compact
                  onChange={(following) => { if (!following) onRemoved?.(f.id); }}
                />
                <InlineShareButton
                  variant="compact"
                  testid={`share-followed-${f.id}`}
                  url={shareUrl}
                  previewPath={`/incident/${f.id}/share`}
                  navigateTo={`/incident/${f.id}`}
                  title={`${f.name} — Firewatch`}
                  caption={buildIncidentCaption({
                    name: f.name,
                    county: f.county,
                    acres: f.acres,
                    statusLabel: f.status_label,
                    incNum: f.inc_num,
                    resources: f.resources,
                    evacs: evacs,
                    lastSharedAt: shareLogs[f.id] || null,
                    updatedAt: f.date,
                    url: shareUrl,
                  })}
                  text={
                    `${f.type || "Incident"} · ${f.name}` +
                    (f.acres != null ? ` · ${formatAcres(f.acres)} ac` : "") +
                    " · via Okanogan Highlands Firewatch"
                  }
                />
              </div>
            );
          })()}
        </div>
        );
      })}
    </div>
  );
};

export default FollowedFiresPanel;
